import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '@/hooks/useLang';
import { buildLangPath, stripLangPrefix } from '@/utils/routeBuilders';

const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dental-tourism');
  const { lang } = useLang();

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const scrollToForm = () => {
      const formElement = document.querySelector('form');
      if (formElement) {
        const yOffset = -100; // Offset to account for fixed header
        const y =
          formElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    if (stripLangPrefix(window.location.pathname) !== '/') {
      navigate(buildLangPath(lang, '/'));
      setTimeout(scrollToForm, 100);
    } else {
      scrollToForm();
    }
  };

  return (
    <section className="pt-28 pb-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-dental-purple/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-ocean-blue/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2 reveal" data-direction="left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 text-gradient-primary inline-block relative after:content-[''] after:absolute after:-bottom-2 after:left-1/4 after:right-1/4 after:h-1 after:bg-dental-tertiary after:rounded-full reveal" data-direction="up">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-dental-dark/80 mb-8 max-w-xl">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href={buildLangPath(lang, '/')}
                className="btn-primary group"
                onClick={handleContactClick}
              >
                {t('hero.ctas.primary')}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a 
                href="#process" 
                className="btn-outline group"
              >
                {t('hero.ctas.secondary')}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
          <div className="lg:w-1/2 reveal" data-direction="right">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-dental-purple/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-ocean-blue/20 rounded-full blur-xl"></div>
              <div className="relative z-10 bg-white p-2 rounded-2xl shadow-xl shadow-dental-dark/5 border border-dental-light/20">
                <img 
                  src="/images/Airplane Landing.jpg"
                  alt={t('hero.imageAlt')}
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
